<template>
    <vx-modal v-model:visible="visible" padding="standard" size="md">
        <template v-slot:header>Edit Contact</template>

        <div>
            <div class="flex flex-col gap-4">
                <vx-alert v-if="error" color="danger" :closeable="false">{{ error }}</vx-alert>
                <div>
                    <vx-label size="lg">First Name</vx-label>
                    <vx-input v-model="firstName" size="lg" />
                </div>
                <div>
                    <vx-label size="lg">Last Name</vx-label>
                    <vx-input v-model="lastName" size="lg" />
                </div>
                <template v-if="profileTarget">
                    <div>
                        <vx-label size="lg">Email</vx-label>
                        <vx-input v-model="email" size="lg" />
                    </div>
                    <div>
                        <vx-label size="lg">Entry Channel</vx-label>
                        <vx-input v-model="entryChannel" size="lg" />
                    </div>
                    <div>
                        <vx-label size="lg">Membership Status</vx-label>
                        <vx-input v-model="membershipStatus" size="lg" />
                    </div>
                    <div>
                        <vx-label size="lg">Lifecycle Status</vx-label>
                        <vx-input v-model="lifecycleStatus" size="lg" />
                    </div>
                </template>
            </div>
        </div>
        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="
                        error = '';
                        visible = false;
                    "
                    color="muted"
                    size="lg"
                    type="button"
                    :disabled="saving"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="submit"
                    type="button"
                    color="primary"
                    size="lg"
                    class="grow"
                    :loading="saving"
                    data-test="submit"
                >
                    Save
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>
<script setup>
import { VxModal, VxAlert, VxButton, VxInput, VxLabel } from '@voxie/frontend-components';
import { computed, inject, ref, watch } from 'vue';
import { useContactDetails } from '~/components/contacts/contact/useContactDetails';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { catchRequestError } from '~/utils/request';

const visible = defineModel({
    default: false,
});

const FEATURES = inject('FEATURES');

const { catchError } = useFormRequestHandler();
const { contact, save, attributeSets, selectedProfileId, saveAttributeSets } = useContactDetails();

const profileTarget = computed(() => {
    if (!FEATURES.local_attributes || !attributeSets.value) {
        return null;
    }

    const profile =
        selectedProfileId.value && selectedProfileId.value !== 'corporate'
            ? attributeSets.value.groups.find((p) => p.group_id === selectedProfileId.value)
            : attributeSets.value.corporate;

    return profile?.editable ? profile : null;
});

const saving = ref(false);
const error = ref('');
const firstName = ref('');
const lastName = ref('');
const email = ref('');
const entryChannel = ref('');
const membershipStatus = ref('');
const lifecycleStatus = ref('');

const submit = async () => {
    if (saving.value) {
        return;
    }

    saving.value = true;

    const originalFirstName = contact.value.first_name;
    const originalLastName = contact.value.last_name;
    const originalProfile = profileTarget.value
        ? {
              email: profileTarget.value.email,
              entry_channel: profileTarget.value.entry_channel,
              membership_status: profileTarget.value.membership_status,
              lifecycle_status: profileTarget.value.lifecycle_status,
          }
        : null;
    let savedContact = false;

    try {
        contact.value.first_name = firstName.value;
        contact.value.last_name = lastName.value;

        await save();
        savedContact = true;

        if (profileTarget.value) {
            profileTarget.value.email = email.value;
            profileTarget.value.entry_channel = entryChannel.value;
            profileTarget.value.membership_status = membershipStatus.value;
            profileTarget.value.lifecycle_status = lifecycleStatus.value;

            await saveAttributeSets();
        }

        visible.value = false;
    } catch (e) {
        catchError(e);
        error.value = catchRequestError(e);

        if (!savedContact) {
            contact.value.first_name = originalFirstName;
            contact.value.last_name = originalLastName;
        } else if (profileTarget.value) {
            profileTarget.value.email = originalProfile.email;
            profileTarget.value.entry_channel = originalProfile.entry_channel;
            profileTarget.value.membership_status = originalProfile.membership_status;
            profileTarget.value.lifecycle_status = originalProfile.lifecycle_status;
        }
    } finally {
        saving.value = false;
    }
};

watch(
    [() => visible.value, () => profileTarget.value],
    ([, target], [wasVisible, previousTarget]) => {
        if (wasVisible && previousTarget && target !== previousTarget) {
            return;
        }

        firstName.value = contact.value.first_name;
        lastName.value = contact.value.last_name;
        email.value = target?.email;
        entryChannel.value = target?.entry_channel;
        membershipStatus.value = target?.membership_status;
        lifecycleStatus.value = target?.lifecycle_status;
    },
    {
        immediate: true,
    }
);
</script>
